import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Edit3,
  Loader2,
  Mail,
  Megaphone,
  MessageCircle,
  Send,
  Trash2,
  Truck,
  Users,
  WalletCards,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { Period } from "../funnel-detail/types";
import {
  financeService,
  getFinanceLogisticsOverview,
  type FinanceReportChannel,
  type FinanceReportRecipient,
} from "../../services/financeService";

type ReportsTabProps = {
  period: Period;
};

type ScheduleId = "daily" | "weekly" | "monthly";
type StepId = "schedule" | "metrics" | "channels" | "preview";
type MetricGroupId = "financial" | "marketing" | "logistics";

type ScheduleConfig = {
  id: ScheduleId;
  label: string;
};

type MetricOption = {
  id: string;
  label: string;
  group: MetricGroupId;
};

type ReportConfig = {
  frequencies: ScheduleId[];
  sendTime: string;
  channels: FinanceReportChannel[];
  groups: Record<MetricGroupId, boolean>;
  metrics: string[];
  recipients: FinanceReportRecipient[];
  countryCode: string;
};

type StoredReportConfig = Partial<ReportConfig> & {
  frequency?: string;
  whatsappConsent?: boolean;
};

const steps: Array<{ id: StepId; label: string }> = [
  { id: "schedule", label: "Horario" },
  { id: "metrics", label: "Métricas" },
  { id: "channels", label: "Canales" },
  { id: "preview", label: "Listo" },
];

const scheduleOptions: ScheduleConfig[] = [
  { id: "daily", label: "Diario" },
  { id: "weekly", label: "Semanal" },
  { id: "monthly", label: "Mensual" },
];

const groupOptions: Array<{
  id: MetricGroupId;
  title: string;
  compactTitle: string;
  icon: LucideIcon;
}> = [
  {
    id: "financial",
    title: "Métricas financieras",
    compactTitle: "Finanzas",
    icon: WalletCards,
  },
  {
    id: "marketing",
    title: "Marketing / Meta Ads",
    compactTitle: "Marketing",
    icon: Megaphone,
  },
  {
    id: "logistics",
    title: "Logística",
    compactTitle: "Logística",
    icon: Truck,
  },
];

const metricOptions: MetricOption[] = [
  { id: "revenue", label: "Recaudo", group: "financial" },
  { id: "orders_created", label: "Órdenes", group: "financial" },
  { id: "average_ticket", label: "Ticket promedio", group: "financial" },
  { id: "net_profit", label: "Utilidad estimada", group: "financial" },
  { id: "net_margin", label: "Margen neto", group: "financial" },
  { id: "ad_spend", label: "Inversión en pauta", group: "marketing" },
  { id: "cpa", label: "CPA", group: "marketing" },
  { id: "roas", label: "ROAS", group: "marketing" },
  { id: "ctr", label: "CTR", group: "marketing" },
  { id: "attributed_purchases", label: "Compras atribuidas", group: "marketing" },
  { id: "dispatched_orders", label: "Despachados", group: "logistics" },
  { id: "pending_dispatch", label: "Pendientes por despachar", group: "logistics" },
  { id: "delivered_orders", label: "Entregados", group: "logistics" },
  { id: "returns", label: "Rechazados / devueltos", group: "logistics" },
  { id: "delivery_rate", label: "Tasa de entrega", group: "logistics" },
];

const countryPrefixes = [
  { code: "CO", label: "Colombia", prefix: "+57" },
  { code: "MX", label: "Mexico", prefix: "+52" },
  { code: "US", label: "Estados Unidos", prefix: "+1" },
  { code: "PE", label: "Peru", prefix: "+51" },
  { code: "CL", label: "Chile", prefix: "+56" },
];

const defaultConfig: ReportConfig = {
  frequencies: [],
  sendTime: "",
  channels: ["email"],
  groups: {
    financial: false,
    marketing: false,
    logistics: false,
  },
  metrics: [],
  recipients: [{ label: "", email: "", phone: "" }],
  countryCode: "CO",
};

const defaultExpandedGroups: Record<MetricGroupId, boolean> = {
  financial: false,
  marketing: false,
  logistics: false,
};

const normalizeFrequencies = (stored: StoredReportConfig): ScheduleId[] => {
  const raw = stored.frequencies?.length ? stored.frequencies : stored.frequency?.split(",");
  const normalized = (raw ?? defaultConfig.frequencies).filter((item): item is ScheduleId =>
    scheduleOptions.some((option) => option.id === item)
  );
  return normalized;
};

export function ReportsTab({ period }: ReportsTabProps) {
  const [config, setConfig] = useState<ReportConfig>(defaultConfig);
  const [configured, setConfigured] = useState(false);
  const [step, setStep] = useState<StepId>("schedule");
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState<Record<MetricGroupId, boolean>>(defaultExpandedGroups);
  const [dropiConnected, setDropiConnected] = useState(false);
  const [checkingDropi, setCheckingDropi] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [started, setStarted] = useState(false);

  const stepIndex = steps.findIndex((item) => item.id === step);
  const selectedPrefix = countryPrefixes.find((country) => country.code === config.countryCode)?.prefix ?? "+57";

  const selectedMetrics = useMemo(
    () =>
      config.metrics.filter((metricId) => {
        const metric = metricOptions.find((item) => item.id === metricId);
        if (!metric) return false;
        if (!config.groups[metric.group]) return false;
        if (metric.group === "logistics" && !dropiConnected) return false;
        return true;
      }),
    [config.groups, config.metrics, dropiConnected]
  );

  const selectedMetricLabels = useMemo(
    () => selectedMetrics.map((metricId) => metricOptions.find((metric) => metric.id === metricId)?.label ?? metricId),
    [selectedMetrics]
  );

  const normalizedRecipients = useMemo(
    () =>
      config.recipients.map((recipient) => ({
        ...recipient,
        phone: recipient.phone?.trim()
          ? `${selectedPrefix}${recipient.phone.replace(/\D/g, "")}`
          : recipient.phone,
      })),
    [config.recipients, selectedPrefix]
  );

  const request = useMemo(
    () => ({
      period,
      frequency: config.frequencies.join(","),
      send_time: config.sendTime,
      channels: config.channels,
      sections: selectedMetrics,
      recipients: normalizedRecipients,
    }),
    [config.channels, config.frequencies, config.sendTime, normalizedRecipients, period, selectedMetrics]
  );

  const canUseWhatsapp = config.channels.includes("whatsapp");
  const hasEmailRecipient = config.recipients.some((recipient) => Boolean(recipient.email?.trim()));
  const hasPhoneRecipient = config.recipients.some((recipient) => Boolean(recipient.phone?.trim()));
  const scheduleComplete = config.frequencies.length > 0 && Boolean(config.sendTime);
  const metricsComplete = selectedMetrics.length > 0;
  const channelsComplete = hasEmailRecipient && (!canUseWhatsapp || hasPhoneRecipient);

  useEffect(() => {
    let mounted = true;
    setLoadingConfig(true);
    financeService.getFinanceReportConfig()
      .then((saved) => {
        if (!mounted) return;
        const savedGroups = metricOptions.reduce(
          (acc, metric) => ({
            ...acc,
            [metric.group]: acc[metric.group] || saved.sections.includes(metric.id),
          }),
          { ...defaultConfig.groups }
        );
        const nextConfig: ReportConfig = {
          ...defaultConfig,
          frequencies: normalizeFrequencies({ frequency: saved.frequency }),
          sendTime: saved.send_time,
          channels: saved.channels?.length ? saved.channels : defaultConfig.channels,
          groups: savedGroups,
          metrics: saved.sections ?? [],
          recipients: saved.recipients?.length ? saved.recipients : defaultConfig.recipients,
          countryCode: saved.country_code || "CO",
        };
        setConfig(nextConfig);
        setConfigured(saved.configured);
        setStep(saved.configured ? "preview" : "schedule");
        setMaxUnlockedStep(saved.configured ? steps.length - 1 : 0);
        setExpandedGroups(savedGroups);
      })
      .catch(() => {
        if (mounted) toast.error("No pudimos cargar la configuración de reportes");
      })
      .finally(() => {
        if (mounted) setLoadingConfig(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setCheckingDropi(true);
    getFinanceLogisticsOverview(period).then((overview) => {
      if (!mounted) return;
      setDropiConnected(Boolean(overview.dropi_connected));
      setCheckingDropi(false);
    });
    return () => {
      mounted = false;
    };
  }, [period]);

  useEffect(() => {
    if (dropiConnected) return;
    setConfig((current) => ({
      ...current,
      groups: { ...current.groups, logistics: false },
      metrics: current.metrics.filter((metricId) => metricOptions.find((metric) => metric.id === metricId)?.group !== "logistics"),
    }));
  }, [dropiConnected]);

  function updateConfig(patch: Partial<ReportConfig>) {
    setConfig((current) => ({ ...current, ...patch }));
  }

  const canNavigateToStep = (index: number) => {
    if (index > maxUnlockedStep) return false;
    if (index >= 1 && !scheduleComplete) return false;
    if (index >= 2 && !metricsComplete) return false;
    if (index >= 3 && !channelsComplete) return false;
    return true;
  };

  const goToStep = (nextStep: StepId, index: number) => {
    if (!canNavigateToStep(index)) return;
    setStep(nextStep);
  };

  const validateCurrentStep = () => {
    if (step === "schedule" && !scheduleComplete) {
      toast.error("Selecciona al menos una frecuencia y una hora");
      return false;
    }
    if (step === "metrics" && !metricsComplete) {
      toast.error("Selecciona al menos una métrica");
      return false;
    }
    if ((step === "channels" || step === "preview") && !hasEmailRecipient) {
      toast.error("Agrega al menos un correo para enviar el reporte");
      return false;
    }
    if ((step === "channels" || step === "preview") && canUseWhatsapp && !hasPhoneRecipient) {
      toast.error("Agrega al menos un teléfono para WhatsApp");
      return false;
    }
    return true;
  };

  const toggleFrequency = (frequency: ScheduleId) => {
    const exists = config.frequencies.includes(frequency);
    const next = exists
      ? config.frequencies.filter((item) => item !== frequency)
      : [...config.frequencies, frequency];
    if (next.length === 0) {
      toast.error("Deja al menos una frecuencia activa");
      return;
    }
    updateConfig({ frequencies: next });
  };

  const toggleGroup = (groupId: MetricGroupId) => {
    if (groupId === "logistics" && !dropiConnected) return;
    const nextActive = !config.groups[groupId];
    const groupMetricIds = metricOptions.filter((metric) => metric.group === groupId).map((metric) => metric.id);
    updateConfig({
      groups: { ...config.groups, [groupId]: nextActive },
      metrics: nextActive
        ? [...new Set([...config.metrics, ...groupMetricIds])]
        : config.metrics.filter((metricId) => !groupMetricIds.includes(metricId)),
    });
    setExpandedGroups((current) => ({ ...current, [groupId]: nextActive || current[groupId] }));
  };

  const toggleMetric = (metricId: string) => {
    updateConfig({
      metrics: config.metrics.includes(metricId)
        ? config.metrics.filter((item) => item !== metricId)
        : [...config.metrics, metricId],
    });
  };

  const toggleExpandedGroup = (groupId: MetricGroupId) => {
    setExpandedGroups((current) => ({ ...current, [groupId]: !current[groupId] }));
  };

  const toggleChannel = (channel: FinanceReportChannel) => {
    if (channel === "email") return;
    updateConfig({
      channels: config.channels.includes(channel)
        ? config.channels.filter((item) => item !== channel)
        : [...config.channels, channel],
    });
  };

  const updateRecipient = (index: number, patch: Partial<FinanceReportRecipient>) => {
    updateConfig({
      recipients: config.recipients.map((recipient, itemIndex) =>
        itemIndex === index ? { ...recipient, ...patch } : recipient
      ),
    });
  };

  const addRecipient = () => {
    updateConfig({
      recipients: [...config.recipients, { label: "Nuevo destinatario", email: "", phone: "" }],
    });
  };

  const removeRecipient = (index: number) => {
    updateConfig({
      recipients: config.recipients.filter((_, itemIndex) => itemIndex !== index),
    });
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;
    if (step === "preview") {
      setSavingConfig(true);
      try {
        const saved = await financeService.saveFinanceReportConfig({
          frequency: config.frequencies.join(","),
          send_time: config.sendTime,
          country_code: config.countryCode,
          channels: config.channels,
          sections: selectedMetrics,
          recipients: normalizedRecipients,
        });
        setConfigured(saved.configured);
        setMaxUnlockedStep(steps.length - 1);
        toast.success("Configuración guardada");
      } catch {
        toast.error("No pudimos guardar la configuración");
      } finally {
        setSavingConfig(false);
      }
      return;
    }
    const nextIndex = Math.min(stepIndex + 1, steps.length - 1);
    setMaxUnlockedStep((current) => Math.max(current, nextIndex));
    setStep(steps[nextIndex].id);
  };

  const handleBack = () => {
    setStep(steps[Math.max(stepIndex - 1, 0)].id);
  };

  const handleEditConfig = () => {
    setMaxUnlockedStep(0);
    setStep("schedule");
  };

  const handleSendTest = async () => {
    if (!validateCurrentStep()) return;

    setSending(true);
    try {
      const result = await financeService.sendFinanceReportTest(request);
      const sent = result.deliveries.some((delivery) => delivery.channel === "email" && delivery.status === "sent");
      const whatsappPending = result.deliveries.some(
        (delivery) => delivery.channel === "whatsapp" && delivery.status === "pending_provider"
      );
      if (sent && whatsappPending) toast.success("Correo enviado. WhatsApp queda listo para proveedor.");
      else if (sent) toast.success("Correo de prueba enviado");
      else toast("Revisa destinatarios y canales antes de enviar");
    } catch {
      toast.error("No pudimos enviar el correo de prueba");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-full max-w-[1040px] space-y-5">
      {!started ? (
        <section id="finance-reports-intro-target" className="rounded-lg border border-gray-200 bg-white px-8 py-8 shadow-sm">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#2b5ce6]">Reportes automáticos</p>
            <h2 className="mt-2 text-2xl font-bold text-gray-950">Configura tus reportes de Fluxi Finance</h2>
            <p className="mt-3 text-sm leading-6 text-gray-500">
              Define qué métricas quieres recibir, a qué hora y en qué canales. Finance te avisará lo importante por
              correo y WhatsApp sin que tengas que entrar todos los días a revisar.
            </p>
            <button
              type="button"
              onClick={() => setStarted(true)}
              className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#2b5ce6] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#214ec7]"
            >
              Iniciar configuración
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      ) : null}

      {started ? (
        <div id="finance-reports-builder-target" className="flex w-full min-w-0 items-start">
          <aside id="finance-reports-steps-target" className="hidden w-[190px] shrink-0 md:block">
        <nav className="sticky top-0 space-y-1 pr-5">
          {steps.map((item, index) => {
            const isActive = item.id === step;
            const disabled = !canNavigateToStep(index);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => goToStep(item.id, index)}
                disabled={disabled}
                className={`flex h-10 w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-white text-[#2b5ce6] ring-1 ring-[#2b5ce6]"
                    : disabled
                    ? "cursor-not-allowed text-gray-300"
                    : "text-gray-500 hover:bg-white/70 hover:text-gray-950"
                }`}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs">
                  {index + 1}
                </span>
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>
          </aside>

          <main className="flex min-w-0 flex-1 flex-col border-l border-gray-200/70">
        <div className="border-b border-gray-200/70 bg-[#fbfcff] px-1 pb-3 md:hidden">
          <div className="flex gap-2 overflow-x-auto">
            {steps.map((item, index) => {
              const isActive = item.id === step;
              const disabled = !canNavigateToStep(index);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goToStep(item.id, index)}
                  disabled={disabled}
                  className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-lg px-3 text-sm font-semibold ${
                    isActive
                      ? "border border-[#2b5ce6] bg-blue-50 text-[#2b5ce6]"
                      : disabled
                      ? "bg-white text-gray-300 ring-1 ring-gray-100"
                      : "bg-white text-gray-600 ring-1 ring-gray-200"
                  }`}
                >
                  <span>{index + 1}</span>
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 px-1 py-4 md:px-7 md:py-0">
          {step === "schedule" ? (
            <ScheduleStep config={config} onChange={updateConfig} onToggleFrequency={toggleFrequency} />
          ) : null}
          {step === "metrics" ? (
            <MetricsStep
              config={config}
              checkingDropi={checkingDropi}
              dropiConnected={dropiConnected}
              expandedGroups={expandedGroups}
              onToggleExpandedGroup={toggleExpandedGroup}
              onToggleGroup={toggleGroup}
              onToggleMetric={toggleMetric}
            />
          ) : null}
          {step === "channels" ? (
            <ChannelsStep
              config={config}
              onAddRecipient={addRecipient}
              onChange={updateConfig}
              onRemoveRecipient={removeRecipient}
              onToggleChannel={toggleChannel}
              onUpdateRecipient={updateRecipient}
            />
          ) : null}
          {step === "preview" ? (
            <ReadyStep
              config={config}
              dropiConnected={dropiConnected}
              selectedMetricLabels={selectedMetricLabels}
              selectedMetrics={selectedMetrics}
            />
          ) : null}

          <div className="mt-6 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleBack}
              disabled={stepIndex === 0}
              className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Atrás
            </button>
            <div className="flex flex-col gap-3 sm:flex-row">
              {step === "preview" ? (
                <>
                  <button
                    type="button"
                    onClick={handleEditConfig}
                    className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                  >
                    <Edit3 className="h-4 w-4" />
                    Editar configuración
                  </button>
                  <button
                    type="button"
                    onClick={handleSendTest}
                    disabled={sending}
                    className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Enviar prueba
                  </button>
                </>
              ) : null}
              <button
                type="button"
                onClick={handleNext}
                disabled={loadingConfig || savingConfig}
                className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-[#2b5ce6] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#214ec7] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingConfig ? "Guardando..." : step === "preview" ? (configured ? "Guardar cambios" : "Guardar configuración") : "Continuar"}
                {step === "preview" ? <CheckCircle2 className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
          </main>
        </div>
      ) : null}
    </div>
  );
}

function ScheduleStep({
  config,
  onChange,
  onToggleFrequency,
}: {
  config: ReportConfig;
  onChange: (patch: Partial<ReportConfig>) => void;
  onToggleFrequency: (frequency: ScheduleId) => void;
}) {
  return (
    <div>
      <StepHeader title="Frecuencia y horario" />
      <div className="max-w-[760px] space-y-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {scheduleOptions.map((option) => (
            <label
              key={option.id}
              className="flex h-11 cursor-pointer items-center gap-2 rounded-lg bg-white px-3 text-sm font-semibold text-gray-900 ring-1 ring-gray-200 transition hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={config.frequencies.includes(option.id)}
                onChange={() => onToggleFrequency(option.id)}
                className="h-4 w-4 rounded border-gray-300 text-[#2b5ce6] focus:ring-[#2b5ce6]"
              />
              {option.label}
            </label>
          ))}
        </div>
        <label className="block max-w-[220px] text-sm font-semibold text-gray-700">
          Hora de envío
          <input
            value={config.sendTime}
            onChange={(event) => onChange({ sendTime: event.target.value })}
            type="time"
            className="mt-2 h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-gray-800 outline-none focus:border-[#2b5ce6]"
          />
        </label>
      </div>
    </div>
  );
}

function MetricsStep({
  config,
  checkingDropi,
  dropiConnected,
  expandedGroups,
  onToggleExpandedGroup,
  onToggleGroup,
  onToggleMetric,
}: {
  config: ReportConfig;
  checkingDropi: boolean;
  dropiConnected: boolean;
  expandedGroups: Record<MetricGroupId, boolean>;
  onToggleExpandedGroup: (groupId: MetricGroupId) => void;
  onToggleGroup: (groupId: MetricGroupId) => void;
  onToggleMetric: (metricId: string) => void;
}) {
  return (
    <div>
      <StepHeader title="Métricas" />
      <div className="max-w-[760px] space-y-2">
        {groupOptions.map((group) => {
          const metrics = metricOptions.filter((metric) => metric.group === group.id);
          const selectedCount = metrics.filter((metric) => config.metrics.includes(metric.id)).length;
          const groupDisabled = group.id === "logistics" && !dropiConnected;
          const groupActive = config.groups[group.id] && !groupDisabled;
          const Icon = group.icon;
          const expanded = expandedGroups[group.id];

          return (
            <section key={group.id} className="rounded-lg bg-white ring-1 ring-gray-200">
              <div className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={groupActive}
                  disabled={groupDisabled}
                  onChange={() => onToggleGroup(group.id)}
                  className="h-4 w-4 rounded border-gray-300 text-[#2b5ce6] focus:ring-[#2b5ce6] disabled:opacity-40"
                  aria-label={`Activar ${group.title}`}
                />
                <button
                  type="button"
                  onClick={() => onToggleExpandedGroup(group.id)}
                  className="flex min-w-0 items-center gap-2 text-left"
                >
                  <Icon className={`h-4 w-4 shrink-0 ${groupActive ? "text-gray-500" : "text-gray-300"}`} />
                  <span className={`truncate text-sm font-semibold ${groupDisabled ? "text-gray-400" : "text-gray-950"}`}>
                    {group.title}
                  </span>
                </button>
                <span className="text-xs font-medium text-gray-400">
                  {groupDisabled
                    ? checkingDropi
                      ? "Revisando"
                      : "Sin Dropi"
                    : `${selectedCount}/${metrics.length}`}
                </span>
                <button
                  type="button"
                  onClick={() => onToggleExpandedGroup(group.id)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-50"
                  aria-label={expanded ? "Contraer bloque" : "Expandir bloque"}
                >
                  {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
              {expanded ? (
                <div className="grid grid-cols-1 gap-2 border-t border-gray-100 px-3 py-3 sm:grid-cols-2 lg:grid-cols-3">
                  {metrics.map((metric) => (
                    <label
                      key={metric.id}
                      className={`flex h-9 min-w-0 cursor-pointer items-center gap-2 rounded-lg px-2 text-xs font-semibold ring-1 ${
                        groupActive && config.metrics.includes(metric.id)
                          ? "bg-blue-50 text-[#2b5ce6] ring-blue-100"
                          : "bg-white text-gray-500 ring-gray-200"
                      } ${groupDisabled || !groupActive ? "pointer-events-none opacity-45" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={config.metrics.includes(metric.id) && groupActive}
                        onChange={() => onToggleMetric(metric.id)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-[#2b5ce6] focus:ring-[#2b5ce6]"
                      />
                      <span className="truncate">{metric.label}</span>
                    </label>
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function ChannelsStep({
  config,
  onAddRecipient,
  onChange,
  onRemoveRecipient,
  onToggleChannel,
  onUpdateRecipient,
}: {
  config: ReportConfig;
  onAddRecipient: () => void;
  onChange: (patch: Partial<ReportConfig>) => void;
  onRemoveRecipient: (index: number) => void;
  onToggleChannel: (channel: FinanceReportChannel) => void;
  onUpdateRecipient: (index: number, patch: Partial<FinanceReportRecipient>) => void;
}) {
  return (
    <div>
      <StepHeader title="Canales y destinatarios" />
      <div className="grid max-w-[760px] grid-cols-1 gap-2 sm:grid-cols-2">
        <ChannelOption
          active
          icon={Mail}
          title="Email"
          detail="Prueba disponible."
          onClick={() => onToggleChannel("email")}
        />
        <ChannelOption
          active={config.channels.includes("whatsapp")}
          icon={MessageCircle}
          title="WhatsApp"
          detail="Preparado para activar luego."
          onClick={() => onToggleChannel("whatsapp")}
        />
      </div>

      <div className="mt-5 max-w-[760px]">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xs font-semibold tracking-normal text-gray-400">Destinatarios</h2>
            <p className="mt-1 text-sm leading-5 text-gray-500">Agrega los contactos que recibirán el reporte.</p>
          </div>
          <button
            type="button"
            onClick={onAddRecipient}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            <Users className="h-4 w-4" />
            Agregar
          </button>
        </div>

        <div className="space-y-2">
          {config.recipients.map((recipient, index) => (
            <div key={`${recipient.label}-${index}`} className="rounded-lg bg-white p-3 ring-1 ring-gray-200">
              <div className="grid grid-cols-1 gap-2 lg:grid-cols-[minmax(0,.85fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,.85fr)_minmax(0,1fr)_240px_36px]">
                <label className="text-xs font-semibold text-gray-400">
                  Nombre
                  <input
                    value={recipient.label}
                    onChange={(event) => onUpdateRecipient(index, { label: event.target.value })}
                    placeholder="Equipo financiero"
                    className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-800 outline-none focus:border-[#2b5ce6]"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-400">
                  Correo
                  <input
                    value={recipient.email ?? ""}
                    onChange={(event) => onUpdateRecipient(index, { email: event.target.value })}
                    placeholder="correo@empresa.com"
                    className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm text-gray-800 outline-none focus:border-[#2b5ce6]"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-400">
                  Celular
                  <div className="mt-1 flex">
                    <select
                      value={config.countryCode}
                      onChange={(event) => onChange({ countryCode: event.target.value })}
                      className="h-9 w-[86px] rounded-l-lg border border-r-0 border-gray-200 bg-gray-50 px-2 text-xs font-semibold text-gray-500 outline-none focus:border-[#2b5ce6]"
                      aria-label="Indicativo"
                    >
                      {countryPrefixes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.prefix}
                        </option>
                      ))}
                    </select>
                    <input
                      value={recipient.phone ?? ""}
                      onChange={(event) => onUpdateRecipient(index, { phone: event.target.value })}
                      placeholder="300 000 0000"
                      className="h-9 min-w-0 flex-1 rounded-r-lg border border-gray-200 px-3 text-sm text-gray-800 outline-none focus:border-[#2b5ce6]"
                    />
                  </div>
                </label>
                <button
                  type="button"
                  onClick={() => onRemoveRecipient(index)}
                  className="mt-5 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-400 shadow-sm transition hover:bg-gray-50 hover:text-red-500"
                  aria-label="Quitar destinatario"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReadyStep({
  config,
  dropiConnected,
  selectedMetricLabels,
  selectedMetrics,
}: {
  config: ReportConfig;
  dropiConnected: boolean;
  selectedMetricLabels: string[];
  selectedMetrics: string[];
}) {
  const activeGroups = groupOptions
    .filter((group) => config.groups[group.id] && (group.id !== "logistics" || dropiConnected))
    .map((group) => group.compactTitle);

  return (
    <div>
      <StepHeader title="Configuración lista" />
      <div className="grid max-w-[760px] grid-cols-1 gap-2 md:grid-cols-2">
        <SummaryRow label="Frecuencia" value={labelForFrequencies(config.frequencies)} />
        <SummaryRow label="Hora" value={config.sendTime} />
        <SummaryRow label="Bloques" value={activeGroups.length ? activeGroups.join(", ") : "Sin bloques"} />
        <SummaryRow label="Métricas" value={`${selectedMetrics.length} seleccionadas`} />
        <SummaryRow label="Canales" value={config.channels.map(channelLabel).join(", ")} />
        <SummaryRow
          label="Destinatarios"
          value={`${config.recipients.filter((recipient) => recipient.email?.trim()).length} correo(s)`}
        />
      </div>
      <div className="mt-3 max-w-[760px] rounded-lg bg-white p-4 ring-1 ring-gray-200">
        <p className="text-xs font-semibold tracking-normal text-gray-400">Métricas incluidas</p>
        <p className="mt-2 text-sm leading-6 text-gray-700">
          {selectedMetricLabels.length ? selectedMetricLabels.join(", ") : "Sin métricas seleccionadas"}
        </p>
      </div>
    </div>
  );
}

function StepHeader({ title }: { title: string }) {
  return (
    <header className="mb-4">
      <h2 className="text-2xl font-semibold tracking-normal text-gray-950">{title}</h2>
    </header>
  );
}

function ChannelOption({
  active,
  icon: Icon,
  title,
  detail,
  onClick,
}: {
  active: boolean;
  icon: LucideIcon;
  title: string;
  detail: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`grid min-h-16 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-lg bg-white px-3 py-2 text-left ring-1 transition hover:bg-gray-50 ${
        active ? "ring-[#2b5ce6]" : "ring-gray-200"
      }`}
    >
      <Icon className={`h-5 w-5 ${active ? "text-[#2b5ce6]" : "text-gray-400"}`} />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-950">{title}</span>
        <span className="block truncate text-xs text-gray-500">{detail}</span>
      </span>
      {active ? <CheckCircle2 className="h-4 w-4 text-[#2b5ce6]" /> : null}
    </button>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-h-14 items-center justify-between gap-4 rounded-lg bg-white px-4 py-3 ring-1 ring-gray-200">
      <p className="text-xs font-semibold text-gray-400">{label}</p>
      <p className="text-right text-sm font-semibold text-gray-950">{value}</p>
    </div>
  );
}

function labelForFrequencies(frequencies: ScheduleId[]) {
  return scheduleOptions
    .filter((option) => frequencies.includes(option.id))
    .map((option) => option.label)
    .join(", ");
}

function channelLabel(channel: FinanceReportChannel) {
  return channel === "whatsapp" ? "WhatsApp" : "Email";
}

export default ReportsTab;
